(() => {
  const SHAPE_POSITIONS = {
    Round: ['0%', '0%'],
    Oval: ['25%', '0%'],
    Emerald: ['50%', '0%'],
    Pear: ['75%', '0%'],
    Asscher: ['100%', '0%'],
    Marquise: ['0%', '100%'],
    Radiant: ['25%', '100%'],
    Cushion: ['50%', '100%'],
    Princess: ['75%', '100%'],
    Heart: ['100%', '100%'],
  };

  // Average face-up millimeter measurements by shape and carat.
  const FACE_UP_MM = {
    Round: { '1': 6.3, '1.5': 7.3, '2': 8, '2.5': 8.7, '3': 9.2, '3.5': 9.7, '4': 10.2, '4.5': 10.55, '5': 10.9 },
    Oval: { '1': 6.7, '1.5': 7.7, '2': 8.4, '2.5': 9.1, '3': 9.6, '3.5': 10.2, '4': 10.6, '4.5': 10.95, '5': 11.3 },
    Emerald: { '1': 5.7, '1.5': 6.5, '2': 7.2, '2.5': 7.8, '3': 8.2, '3.5': 8.6, '4': 9, '4.5': 9.3, '5': 9.6 },
    Pear: { '1': 7.1, '1.5': 8.1, '2': 8.9, '2.5': 9.6, '3': 10.1, '3.5': 10.8, '4': 11.2, '4.5': 11.6, '5': 12 },
    Asscher: { '1': 5.4, '1.5': 6.2, '2': 6.9, '2.5': 7.4, '3': 8, '3.5': 8.3, '4': 8.7, '4.5': 9.1, '5': 9.5 },
    Marquise: { '1': 7.7, '1.5': 8.7, '2': 9.6, '2.5': 10.3, '3': 11.2, '3.5': 11.6, '4': 12.1, '4.5': 12.75, '5': 13.4 },
    Radiant: { '1': 5.7, '1.5': 6.6, '2': 7.3, '2.5': 7.8, '3': 8.3, '3.5': 8.7, '4': 9.3, '4.5': 9.6, '5': 9.9 },
    Cushion: { '1': 5.6, '1.5': 6.5, '2': 7.2, '2.5': 7.7, '3': 8.2, '3.5': 8.6, '4': 9, '4.5': 9.35, '5': 9.7 },
    Princess: { '1': 5.3, '1.5': 6.2, '2': 6.8, '2.5': 7.4, '3': 7.8, '3.5': 8.3, '4': 8.6, '4.5': 8.85, '5': 9.1 },
    Heart: { '1': 6.4, '1.5': 7.4, '2': 8.1, '2.5': 8.7, '3': 9.2, '3.5': 9.7, '4': 10.4, '4.5': 10.7, '5': 11 },
  };

  // Converts average millimeters to the photographed size-5 finger scale.
  const HAND_SCALE_PERCENT_PER_MM = 0.817;

  const initializeBuilder = (builder) => {
    if (builder.dataset.initialized === 'true') return;
    builder.dataset.initialized = 'true';

    const catalogElement = builder.querySelector('[data-ring-builder-catalog]');
    if (!catalogElement) return;

    let catalog;
    try {
      catalog = JSON.parse(catalogElement.textContent);
    } catch (error) {
      console.error('Unable to load the ring builder catalog.', error);
      return;
    }

    const state = {
      origin: 'lab',
      shape: 'Round',
      carat: null,
      style: null,
      metal: 'yellow',
      purity: '14K',
      previewMode: 'loose',
    };

    const previewImage = builder.querySelector('[data-preview-image]');
    const previewTitle = builder.querySelector('[data-preview-title]');
    const previewMeta = builder.querySelector('[data-preview-meta]');
    const imageLoader = builder.querySelector('[data-image-loader]');
    const loosePreview = builder.querySelector('[data-loose-preview]');
    const handPreview = builder.querySelector('[data-hand-preview]');
    const looseStone = builder.querySelector('[data-loose-stone]');
    const handRing = builder.querySelector('[data-hand-ring]');
    const styleOptions = builder.querySelector('[data-style-options]');
    const styleCount = builder.querySelector('[data-style-count]');
    const summaryTitle = builder.querySelector('[data-summary-title]');
    const summaryMeta = builder.querySelector('[data-summary-meta]');
    const cta = builder.querySelector('[data-builder-cta]');

    const labels = {
      origin: { lab: 'Lab', natural: 'Natural' },
      metal: { yellow: 'yellow gold', white: 'white gold', rose: 'rose gold', platinum: 'platinum' },
    };

    const selectedItem = () => catalog.find((item) => item.shape === state.shape && item.style === state.style);
    const imageMetal = () => state.metal === 'platinum' ? 'white' : state.metal;

    const setSelected = (group, value) => {
      builder.querySelectorAll(`[data-option-group="${group}"] [data-value]`).forEach((button) => {
        const selected = button.dataset.value === value;
        button.classList.toggle('is-selected', selected);
        button.setAttribute('aria-pressed', String(selected));
      });
    };

    const updateDiamondSprites = () => {
      const position = SHAPE_POSITIONS[state.shape];
      looseStone.style.backgroundPosition = `${position[0]} ${position[1]}`;
      handRing.dataset.shape = state.shape.toLowerCase();
      if (state.carat) {
        const faceUpMillimeters = FACE_UP_MM[state.shape][state.carat];
        handRing.style.setProperty('--rb-hand-stone-size', `${(faceUpMillimeters * HAND_SCALE_PERCENT_PER_MM).toFixed(2)}%`);
      }
    };

    const updatePurityControls = () => {
      const goldOptions = builder.querySelectorAll('[data-option-group="purity"] [data-value="14K"], [data-option-group="purity"] [data-value="18K"]');
      const platinumOption = builder.querySelector('[data-option-group="purity"] [data-value="Platinum"]');
      const platinumSelected = state.metal === 'platinum';

      goldOptions.forEach((option) => { option.hidden = platinumSelected; });
      platinumOption.hidden = !platinumSelected;

      if (platinumSelected) {
        state.purity = 'Platinum';
      } else if (state.purity === 'Platinum') {
        state.purity = '14K';
      }
      setSelected('purity', state.purity);
    };

    const renderStyles = () => {
      const available = catalog.filter((item) => item.shape === state.shape);
      const cardMetal = imageMetal();

      if (state.style && !available.some((item) => item.style === state.style)) {
        state.style = null;
      }

      styleOptions.innerHTML = available.map((item) => {
        const selected = item.style === state.style;
        return `
          <button class="ring-builder__style${selected ? ' is-selected' : ''}" type="button" data-style="${item.style}" aria-pressed="${selected}">
            <img src="${item.images[cardMetal]}" alt="${item.shape} ${item.style} engagement ring" loading="lazy">
            <span>${item.style}</span>
          </button>
        `;
      }).join('');

      styleCount.textContent = `${available.length} setting ${available.length === 1 ? 'style' : 'styles'} available for ${state.shape.toLowerCase()}.`;

      styleOptions.querySelectorAll('[data-style]').forEach((button) => {
        button.addEventListener('click', () => {
          state.style = button.dataset.style;
          state.previewMode = 'ring';
          styleOptions.querySelectorAll('[data-style]').forEach((option) => {
            const selected = option.dataset.style === state.style;
            option.classList.toggle('is-selected', selected);
            option.setAttribute('aria-pressed', String(selected));
          });
          updateView();
        });
      });
    };

    const setPreviewMode = (mode) => {
      loosePreview.hidden = mode !== 'loose';
      handPreview.hidden = mode !== 'hand';
      previewImage.hidden = mode !== 'ring';
    };

    const updateSummaryAndLink = () => {
      const origin = labels.origin[state.origin];
      const carat = state.carat ? `${state.carat} ct` : 'Carat not selected';
      const style = state.style || 'Setting not selected';
      const metalName = labels.metal[state.metal];
      const purityName = state.metal === 'platinum' ? '' : `${state.purity} `;

      summaryTitle.textContent = `${origin} · ${state.shape} · ${carat}`;
      summaryMeta.textContent = `${style} · ${purityName}${metalName}`;

      const params = new URLSearchParams({
        diamond: origin,
        shape: state.shape,
        carat,
        style,
        metal: `${purityName}${metalName}`,
      });
      cta.href = `${builder.dataset.consultationUrl}?${params.toString()}`;
    };

    const updateView = () => {
      updateDiamondSprites();
      setPreviewMode(state.previewMode);

      const metalName = labels.metal[state.metal];
      const purityName = state.metal === 'platinum' ? '' : `${state.purity} `;

      if (state.previewMode === 'loose') {
        previewTitle.textContent = `${state.shape} diamond`;
        previewMeta.textContent = 'Choose a carat size to see it on a size 5 finger';
      } else if (state.previewMode === 'hand') {
        previewTitle.textContent = `${state.shape} · ${state.carat} ct`;
        previewMeta.textContent = 'Approximate scale on a US size 5 ring finger';
      } else {
        const item = selectedItem();
        if (item) {
          const imageUrl = new URL(item.images[imageMetal()], window.location.href).href;
          if (previewImage.src !== imageUrl) {
            imageLoader.classList.add('is-loading');
            previewImage.classList.add('is-changing');
            const preload = new Image();
            preload.onload = () => {
              previewImage.src = imageUrl;
              previewImage.classList.remove('is-changing');
              imageLoader.classList.remove('is-loading');
            };
            preload.src = imageUrl;
          }
          previewImage.alt = `${state.carat || ''} carat ${state.shape} ${state.style} engagement ring in ${purityName}${metalName}`.trim();
          previewTitle.textContent = `${state.shape} ${state.style}`;
          previewMeta.textContent = `${labels.origin[state.origin]} · ${state.carat || '—'} ct · ${purityName}${metalName}`;
        }
      }

      updateSummaryAndLink();
    };

    builder.querySelectorAll('[data-option-group]').forEach((group) => {
      const groupName = group.dataset.optionGroup;
      group.querySelectorAll('[data-value]').forEach((button) => {
        button.addEventListener('click', () => {
          state[groupName] = button.dataset.value;
          setSelected(groupName, state[groupName]);

          if (groupName === 'shape') {
            state.carat = null;
            state.style = null;
            state.previewMode = 'loose';
            setSelected('carat', null);
            renderStyles();
          }

          if (groupName === 'carat') {
            state.previewMode = 'hand';
          }

          if (groupName === 'metal') {
            updatePurityControls();
            renderStyles();
            if (state.style) state.previewMode = 'ring';
          }

          if (groupName === 'purity' && state.style) state.previewMode = 'ring';
          updateView();
        });
      });
    });

    renderStyles();
    updatePurityControls();
    updateView();
  };

  document.querySelectorAll('[data-ring-builder]').forEach(initializeBuilder);
  document.addEventListener('shopify:section:load', (event) => {
    const builder = event.target.matches('[data-ring-builder]')
      ? event.target
      : event.target.querySelector('[data-ring-builder]');
    if (builder) initializeBuilder(builder);
  });
})();
